import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import {
  ResetUserStateAction,
  SetAcceptTermsAction,
  SetCurrentUserAction,
  SetShowControlAction,
  SetUserDeviceInfo,
  SetUserFiles,
  SetUserImage,
} from './user.meta.actions'
import { ICurrentUser, UserMetaStateModel } from './user.meta.model'
import { DeviceInfo } from '@capacitor/device'
import { RolesType } from '../../enums/roles.enum'

const INITIAL_STATE: UserMetaStateModel = {
  currentUser: {
    id: '',
    accessToken: '',
    image: null,
    roles: [],
    username: '',
    email: '',
    displayName: '',
    document: '',
    phone: '',
    acceptTerms: false,
    meta: {
      createdAt: new Date(),
      updatedAt: new Date(),
      updatedBy: '',
      createdBy: '',
    },
    isDeleted: false,
    userCategoryIds: [],
  },
  isUserAuthenticated: false,
  showControl: false,
  userDeviceInfo: null,
}

@State<UserMetaStateModel>({
  name: 'userMeta',
  defaults: { ...INITIAL_STATE },
})
@Injectable()
export class UserMetaState {
  @Selector()
  public static getState(state: UserMetaStateModel): UserMetaStateModel {
    return state
  }

  @Selector()
  public static getIsUserAuthenticated(state: UserMetaStateModel): boolean {
    return state.isUserAuthenticated
  }

  @Selector()
  public static getUserToken(state: UserMetaStateModel): string {
    return state.currentUser.accessToken
  }

  @Selector()
  public static getUser(state: UserMetaStateModel): ICurrentUser {
    return state.currentUser
  }

  @Selector()
  public static getUserRoles(state: UserMetaStateModel): RolesType[] {
    return state.currentUser.roles
  }

  @Selector()
  public static getShowControl(state: UserMetaStateModel): boolean {
    return state.showControl
  }

  @Selector()
  public static getUserDeviceInfo(
    state: UserMetaStateModel,
  ): DeviceInfo | null {
    return state.userDeviceInfo
  }

  @Action(ResetUserStateAction)
  resetState(ctx: StateContext<UserMetaStateModel>): void {
    ctx.setState({ ...INITIAL_STATE })
    this.clearUser()
  }

  @Action(SetCurrentUserAction)
  setCurrentUser(
    ctx: StateContext<UserMetaStateModel>,
    action: SetCurrentUserAction,
  ): void {
    const state = ctx.getState()
    action.payload.acceptTerms = action.payload?.acceptTerms || false
    ctx.setState({
      ...state,
      currentUser: action.payload,
      isUserAuthenticated: true,
    })
    this.persistUser({
      ...action.payload,
      isUserAuthenticated: true,
    })
  }

  @Action(SetAcceptTermsAction)
  setAcceptTerms(
    ctx: StateContext<UserMetaStateModel>,
    action: SetAcceptTermsAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      currentUser: {
        ...state.currentUser,
        acceptTerms: action.payload,
      },
    })
    const newState = ctx.getState()
    const updatedUser = {
      ...newState.currentUser,
      isUserAuthenticated: newState.isUserAuthenticated,
    }
    this.persistUser(updatedUser)
  }

  persistUser(user: ICurrentUser): void {
    localStorage.setItem('user', JSON.stringify(user))
  }

  clearUser(): void {
    localStorage.removeItem('user')
  }

  @Action(SetShowControlAction)
  setShowControl(
    ctx: StateContext<UserMetaStateModel>,
    action: SetShowControlAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      showControl: action.payload,
    })
  }

  @Action(SetUserImage)
  setUserImage(
    ctx: StateContext<UserMetaStateModel>,
    action: SetUserImage,
  ): void {
    const state = ctx.getState()
    const image = action.payload
    ctx.setState({
      ...state,
      currentUser: {
        ...state.currentUser,
        image,
      },
    })
  }

  @Action(SetUserFiles)
  setUserFiles(
    ctx: StateContext<UserMetaStateModel>,
    action: SetUserFiles,
  ): void {
    const state = ctx.getState()
    const files = action.payload
    ctx.setState({
      ...state,
      currentUser: {
        ...state.currentUser,
        files,
      },
    })
    const updatedState = ctx.getState()
    const updatedUser = {
      ...updatedState.currentUser,
      isUserAuthenticated: updatedState.isUserAuthenticated,
    }
    this.persistUser(updatedUser)
  }

  @Action(SetUserDeviceInfo)
  setUserDeviceInfo(
    ctx: StateContext<UserMetaStateModel>,
    action: SetUserDeviceInfo,
  ): void {
    const state = ctx.getState()
    const userDeviceInfo = action.payload
    ctx.setState({
      ...state,
      userDeviceInfo,
    })
  }
}
